import { useContext } from 'react'
import {
  SanityBlogPostWithoutBody,
  SanityBlogPostOptions,
} from '@data/sanity/queries/types/blog'
import { getFormattedDate } from '@lib/helpers'
import { LanguageContext } from '@lib/language'
import { PageType, getPageUrl } from '@lib/routes'
import Photo from '@components/photo'
import BaseInternalLink from '@components/link/base-internal-link'

interface BlogPostHeaderProps {
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

const BlogPostHeader = ({ post, options }: BlogPostHeaderProps) => {
  const { locale } = useContext(LanguageContext)

  return (
    <section className="container pt-14 sm:pt-20 md:pt-24 lg:pt-40 flex flex-col justify-center items-center">
      <div className="w-full max-w-3xl mb-6">
        <h1 className="is-h1-hero mb-4">{post.title}</h1>

        {options?.showExcerpt && <p className="text-xl mb-6">{post.excerpt}</p>}

        {options?.showCategories && (
          <div className="flex flex-wrap mt-4">
            {post.categories?.map((category) => (
              <BaseInternalLink
                key={`${post.slug}-category-${category.slug}`}
                href={getPageUrl(PageType.BLOG_CATEGORY, category.slug)}
                role="link"
                className="mr-2 text-[12px] opacity-50"
              >
                {category.name}
              </BaseInternalLink>
            ))}
          </div>
        )}

        {(options?.showAuthorPhoto ||
          options?.showAuthorName ||
          options?.showDate) && (
          <div className="flex items-center mt-4">
            {options?.showAuthorPhoto && post.author?.photo && (
              <Photo
                image={post.author?.photo}
                width={40}
                height={40}
                className="rounded-full overflow-hidden shrink-0 w-[40px] h-[40px] mr-2"
                imageClassName="object-cover"
              />
            )}
            <div>
              {(options?.showAuthorName || options?.showDate) && (
                <p className="text-sm">
                  {options.showAuthorName && (
                    <>
                      {/* TODO: Add hardcoded string to Sanity */}
                      by{' '}
                      <BaseInternalLink
                        href={getPageUrl(
                          PageType.BLOG_AUTHOR,
                          post.author?.slug,
                        )}
                        role="link"
                      >
                        {post.author?.name}
                      </BaseInternalLink>
                    </>
                  )}

                  {options.showDate
                    ? `${options.showAuthorName ? ', ' : ''}${getFormattedDate(
                        post.date,
                        locale,
                      )}`
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {post.coverImage && options?.showCoverImage && (
        <div className="mt-6">
          <Photo
            image={{
              ...post.coverImage,
              borderRadius: 'none',
            }}
          />
        </div>
      )}
    </section>
  )
}

export default BlogPostHeader
